import Task from "../../task/task"
import Goal from "../goal"
import ModelProxy from "../../model-proxy"
import FieldProto from "../../../fields3/proto/field-proto"
import TaskStatusManager from "../../../gd/manager/task-status-manager"
import {ItemIconClass} from "../../../gd/const/item-icon/icon/item-icon-class";

const debug = false
// const debug = "GoalTask"

export default class GoalTask extends Goal {

    public task: Task

    constructor(data: any, useProxy = true) {
        super(data, false)

        this.task = new Task(data['task'] || {})

        return useProxy ? new Proxy(this, ModelProxy) : this
    }

    openGoal() {
        gd.navigation.goToTask(this.task.id)
    }

    get noEntity() {
        return !this.task.id
    }

    get name() {
        return this.task.title
    }

    set name(value: string) {
        this.task.title = value
    }

    get icon() {
        const taskType = gd.session.taskTypes.get(this.task.taskTypeId)
        return taskType ? ItemIconClass[taskType.icon] : 'ii5-task'
    }

    get startDate(): MomentDate | null {
        return this.task?.startDate as MomentDate || null
    }

    set startDate(value: MomentDate | null) {
        const startDate = value
        let endDate

        debug && console.log(debug, 'startDate', value)

        if (startDate && (!this.endDate || startDate?.isAfter(this.endDate, 'day'))) {
            endDate = startDate.clone() as MomentDate
        }
        if (!startDate) {
            endDate = null
        }

        this.task.startDate = startDate

        if (!_.isUndefined(endDate)) {
            this.task.endDate = endDate
        }
    }

    get endDate(): MomentDate | null {
        return (this.task?.endDate || this.task?.deadline || null) as MomentDate | null
    }

    set endDate(value: MomentDate | null) {
        const endDate = value
        let startDate

        // if task endDate is defined and new value is null or if startDate is defined and new value is not null
        // the start/end is updated, otherwise it is deadline
        const isStartEndUpdate = (this.task.endDate && !endDate) || (this.task.startDate && endDate)

        debug && console.log(debug, 'endDate', isStartEndUpdate, value)

        if (isStartEndUpdate) {
            if (this.startDate && endDate?.isBefore(this.startDate, 'day')) {
                startDate = endDate.clone() as MomentDate
            }
            if (!endDate) {
                startDate = null
            }

            this.task.endDate = endDate

            if (!_.isUndefined(startDate)) {
                this.task.startDate = startDate
            }
        } else {
            this.task.deadline = value
        }
    }

    get userId(): ID | null {
        return this.task.actionRequiredUserId || null
    }

    get assigneeUserIds(): ID[] {
        return this.task.assignedToUserId ? [this.task.assignedToUserId] : []
    }

    set assigneeUserIds(values: ID[] | null) {
        // set last selected userId if any
        this.task.assignedToUserId = !_.isNil(values) && !_.isEmpty(values) ? values[values?.length - 1] : null
    }

    get duration():number | null {
        return this.task.durationCalendar
    }

    set duration(value:number | null) {
        this.task.durationCalendar = value
    }

    get durationWorkdays():number | null {
        return this.task.durationWorkdays
    }

    set durationWorkdays(value:number | null) {
        this.task.durationWorkdays = value
    }

    get progress(): number {
        return this.isDone ? 100 : 0 //this.task.progress || 0
    }

    get isDone(): boolean {
        const status = gd.session.statuses.get(this.task.statusId)
        return status && gd.const.systemStatus.isClosed(status.systemStatus) // this.progress === 100
    }

    set isDone(value:boolean) {
        const taskStatuses = TaskStatusManager.getTaskStatuses({projectId:this.task.projectId, taskTypeId:this.task.taskTypeId})

        let newStatus
        if (value) {
            // we need to close - find closed statuses
            const orderedClosedStatuses = _(taskStatuses)
                .filter(m=>[gd.const.systemStatus.COMPLETED, gd.const.systemStatus.CANCELLED].includes(m.systemStatus))
                .sortBy('systemStatus')
                .value()

            debug && console.log(debug, 'isDone orderedClosedStatuses', orderedClosedStatuses)

            newStatus = orderedClosedStatuses[0]
        } else {
            // we need to reopen
            const orderedOpenStatuses = _(taskStatuses)
                .filter(m=>![gd.const.systemStatus.COMPLETED, gd.const.systemStatus.CANCELLED].includes(m.systemStatus))
                .sortBy('systemStatus')
                .value()

            debug && console.log(debug, 'isDone orderedOpenStatuses', orderedOpenStatuses)

            newStatus = orderedOpenStatuses[0]
        }

        debug && console.log(debug, 'isDone newStatus', newStatus)

        this.task.statusId      = newStatus.id
        this.task.systemStatus  = newStatus.systemStatus
    }

    /*get health(): number {
        return this._health;
    }

    set health(value: number) {
        this._health = value
    }*/

    applyDelta(data:any) {
        let isChanged = super.applyDelta(data, ['task'])

        if (data.task) {
            this.task = new Task(data.task)
            isChanged = true
        }

        return isChanged
    }

    async save(): Promise<void> {
        await super.save()

        debug && console.log(debug, 'save', [...this.task.updatedFields])

        if (this.task.updatedFields.has('assignedToUserId')) {
            await gd.api.Task.setAssignedToUser(this.task.id, this.task.assignedToUserId).promise()
            this.task.updatedFields.delete('assignedToUserId')
        }

        if (this.task.updatedFields.has('startDate') && this.task.updatedFields.has('endDate')) {
            await gd.api.Task.startEnd(this.task.id, this.task.startDate, this.task.endDate, this.task.scheduleDate, !!this.task.actionRequiredUserId, this.task.companyId,
                (startDate: MomentDate, endDate: MomentDate, scheduleDate: MomentDate)=>{
                    this.task.startDate = startDate
                    this.task.endDate   = endDate
                if (scheduleDate) {
                    this.task.scheduleStatus    = gd.const.task.scheduleStatus.SCHEDULED;
                    this.task.scheduleDate      = scheduleDate
                }
            }).promise()
            this.task.updatedFields.delete('startDate')
            this.task.updatedFields.delete('endDate')
        }

        if (this.task.updatedFields.has('deadline')) {
            await gd.api.Task.setDeadline(this.task.id, this.task.deadline).promise()
            this.task.updatedFields.delete('deadline')
        }

        if (this.task.updatedFields.has('statusId')) {
            await gd.api.Task.setStatus(this.task.id, this.task.statusId).promise()
            this.task.updatedFields.delete('statusId')
            this.task.updatedFields.delete('systemStatus')
        }

        this.task.save()
    }

    toJSON(): any {
        const json: any = super.toJSON()

        json.task       = this.task.toJSON()

        return json
    }
}
